"use client";
import { contentStore, ContentItem } from "@/lib/store";
import { useStore } from "@/lib/useStore";
import { useState } from "react";
import { Plus, X, GripVertical } from "lucide-react";

type Status = ContentItem["status"];
const COLUMNS: { key: Status; label: string; color: string }[] = [
  { key: "ideas", label: "💡 Ideas", color: "#eab308" },
  { key: "draft", label: "✏️ Draft", color: "#3b82f6" },
  { key: "review", label: "🔍 Review", color: "#a855f7" },
  { key: "ready", label: "✅ Ready", color: "#22c55e" },
  { key: "published", label: "🚀 Published", color: "#6366f1" },
];
const PLATFORMS = ["LinkedIn", "Twitter", "Blog"];

export function ContentPipeline() {
  const items = useStore(contentStore.list);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<ContentItem | null>(null);
  const [dragItem, setDragItem] = useState<string | null>(null);

  const [form, setForm] = useState({
    title: "",
    body: "",
    platforms: [] as string[],
    source: "",
    status: "ideas" as Status,
  });

  const resetForm = () => {
    setForm({ title: "", body: "", platforms: [], source: "", status: "ideas" });
    setShowCreate(false);
  };

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    await contentStore.create({ ...form, createdBy: "Karl" });
    resetForm();
  };

  const handleUpdate = async () => {
    if (!editing) return;
    await contentStore.update(editing.id, {
      title: editing.title,
      body: editing.body,
      platforms: editing.platforms,
      source: editing.source,
      status: editing.status,
    });
    setEditing(null);
  };

  const handleDrop = async (status: Status) => {
    if (!dragItem) return;
    await contentStore.update(dragItem, { status });
    setDragItem(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Content Pipeline</h2>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={16} /> New Idea
        </button>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUMNS.map((col) => {
          const colItems = items
            .filter((i) => i.status === col.key)
            .sort((a, b) => a.order - b.order);
          return (
            <div
              key={col.key}
              className="min-w-[260px] w-[260px] shrink-0 bg-[var(--bg-column)] rounded-xl border border-[var(--border)]"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(col.key)}
            >
              <div className="p-3 border-b border-[var(--border)] flex items-center justify-between">
                <span className="text-sm font-semibold">{col.label}</span>
                <span
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: col.color + "22", color: col.color }}
                >
                  {colItems.length}
                </span>
              </div>
              <div className="p-2 space-y-2 min-h-[200px]">
                {colItems.map((item) => (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={() => setDragItem(item.id)}
                    onClick={() => setEditing({ ...item })}
                    className="bg-[var(--bg-card)] rounded-lg p-3 border border-[var(--border)] hover:border-[var(--accent)] cursor-pointer transition-colors group"
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical size={14} className="mt-0.5 text-[var(--text-muted)] opacity-0 group-hover:opacity-100 shrink-0 cursor-grab" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.title}</p>
                        {item.source && <p className="text-xs text-[var(--text-muted)] mt-1 truncate">{item.source}</p>}
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {item.platforms.map((p) => (
                            <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-hover)] text-[var(--text-muted)]">{p}</span>
                          ))}
                        </div>
                        <div className="mt-2 text-[10px] text-[var(--text-muted)]">
                          {item.createdBy === "Casey" ? "🐕" : "👤"} {item.createdBy}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && (
        <Modal onClose={resetForm} title="New Content Idea">
          <Input placeholder="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <Textarea placeholder="Content body (markdown)" value={form.body} onChange={(v) => setForm({ ...form, body: v })} rows={6} />
          <Input placeholder='Source (e.g. "Coaching call - Feb 16")' value={form.source} onChange={(v) => setForm({ ...form, source: v })} />
          <div className="mt-3">
            <Label>Platforms</Label>
            <div className="flex gap-2">
              {PLATFORMS.map((p) => (
                <TagButton
                  key={p}
                  label={p}
                  active={form.platforms.includes(p)}
                  onClick={() => setForm({ ...form, platforms: form.platforms.includes(p) ? form.platforms.filter((x) => x !== p) : [...form.platforms, p] })}
                />
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Btn variant="ghost" onClick={resetForm}>Cancel</Btn>
            <Btn onClick={handleCreate}>Create</Btn>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title="Edit Content">
          <Input value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
          <Textarea value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} rows={8} />
          <Input value={editing.source} onChange={(v) => setEditing({ ...editing, source: v })} />
          <div className="mt-3">
            <Label>Status</Label>
            <Select value={editing.status} onChange={(v) => setEditing({ ...editing, status: v as Status })} options={COLUMNS.map((c) => ({ value: c.key, label: c.label }))} />
          </div>
          <div className="mt-3">
            <Label>Platforms</Label>
            <div className="flex gap-2">
              {PLATFORMS.map((p) => (
                <TagButton
                  key={p}
                  label={p}
                  active={editing.platforms.includes(p)}
                  onClick={() => setEditing({ ...editing, platforms: editing.platforms.includes(p) ? editing.platforms.filter((x) => x !== p) : [...editing.platforms, p] })}
                />
              ))}
            </div>
          </div>
          <div className="flex justify-between mt-4">
            <Btn variant="danger" onClick={async () => { await contentStore.remove(editing.id); setEditing(null); }}>Delete</Btn>
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
              <Btn onClick={handleUpdate}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Shared UI primitives
function Modal({ children, onClose, title }: { children: React.ReactNode; onClose: () => void; title: string }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text)]"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Input({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return <input placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm mt-2 focus:outline-none focus:border-[var(--accent)]" />;
}

function Textarea({ value, onChange, placeholder, rows }: { value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }) {
  return <textarea placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} rows={rows} className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm mt-2 resize-none focus:outline-none focus:border-[var(--accent)]" />;
}

function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return <select value={value} onChange={(e) => onChange(e.target.value)} className="bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--accent)]">
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>;
}

function Label({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-[var(--text-muted)] mb-1">{children}</p>;
}

function TagButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return <button onClick={onClick} className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${active ? "border-[var(--accent)] bg-[var(--accent)] text-white" : "border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--text-muted)]"}`}>{label}</button>;
}

function Btn({ children, onClick, variant }: { children: React.ReactNode; onClick: () => void; variant?: "ghost" | "danger" }) {
  const cls = variant === "ghost" ? "text-[var(--text-muted)] hover:text-[var(--text)]" : variant === "danger" ? "text-[var(--red)] hover:bg-[var(--red)]/10" : "bg-[var(--accent)] hover:bg-[var(--accent-hover)] font-medium";
  return <button onClick={onClick} className={`px-4 py-2 text-sm rounded-lg transition-colors ${cls}`}>{children}</button>;
}
